import {
  Component,
  computed,
  inject,
  viewChild,
  TemplateRef,
  HostListener,
  effect,
  untracked,
  ElementRef,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import {
  NavbarComponent,
  SidebarComponent,
  SidebarMode,
  MenuItem,
  LoaderComponent,
  CloseIconComponent,
  ArmLogoComponent,
  Modal,
} from '@arm-web/ui-components';
import { LayoutService } from '../../common/services/layout.service';
import { FloatingConfirmationComponent } from '../../common/components/floating-confirmation/floating-confirmation';
import { UnlockFormComponent } from '../../common/components/encryption-forms/unlock-form/unlock-form';
import { LockFormComponent } from '../../common/components/encryption-forms/lock-form/lock-form';
import { GenerateKeyFormComponent } from '../../common/components/encryption-forms/generate-key-form/generate-key-form';
import { AuthService } from '../../common/services/auth.service';
import { MainService } from '../../common/services/main/main.service';
import { EncryptionService } from '../../common/services/encryption.service';
import { NgTemplateOutlet } from '@angular/common';
import { ConfirmationService } from '../../common/services/confirmation.service';
import { Path } from '../../common/constants';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    RouterOutlet,
    NavbarComponent,
    SidebarComponent,
    FloatingConfirmationComponent,
    LoaderComponent,
    CloseIconComponent,
    ArmLogoComponent,
    UnlockFormComponent,
    LockFormComponent,
    GenerateKeyFormComponent,
    Modal,
  ],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.css',
})
export class MainLayoutComponent {
  router = inject(Router);
  private readonly authService = inject(AuthService);
  readonly encryptionService = inject(EncryptionService);
  readonly confirmationService = inject(ConfirmationService);
  mainService = inject(MainService);
  readonly layoutService = inject(LayoutService);

  readonly sidebarMode = SidebarMode;
  readonly drawerId = 'app_navigation_drawer';

  private readonly drawerCloseButton =
    viewChild<ElementRef<HTMLButtonElement>>('drawerCloseButton');
  private drawerReturnFocus: HTMLElement | null = null;

  // Template references
  unlockTemplate = viewChild<TemplateRef<unknown>>('unlockFormTemplate');
  lockTemplate = viewChild<TemplateRef<unknown>>('lockFormTemplate');
  generateKeyTemplate = viewChild<TemplateRef<unknown>>(
    'generateKeyFormTemplate'
  );

  constructor() {
    // Open unlock form when a stale encryption key was cleared.
    effect(() => {
      const nonce = this.encryptionService.unlockPromptNonce();
      if (nonce === 0) {
        return;
      }

      untracked(() => this.openUnlockForm());
    });

    // The drawer only exists on compact screens.
    effect(() => {
      if (!this.layoutService.isCompact()) {
        untracked(() => this.closeDrawer());
      }
    });

    // Covers navigation that does not go through the menu (links, back button).
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.closeDrawer());
  }

  @HostListener('document:keydown.escape')
  onEscPress(): void {
    if (this.layoutService.isDrawerOpen()) {
      this.closeDrawer();
      return;
    }
    this.closeOffCanvas();
  }

  openDrawer(): void {
    this.drawerReturnFocus = document.activeElement as HTMLElement | null;
    this.layoutService.openDrawer();
    // Wait for `inert` to be removed before moving focus into the drawer.
    setTimeout(() => this.drawerCloseButton()?.nativeElement.focus());
  }

  closeDrawer(): void {
    if (!this.layoutService.isDrawerOpen()) return;
    this.layoutService.closeDrawer();
    this.drawerReturnFocus?.focus();
    this.drawerReturnFocus = null;
  }

  // Menu items configuration
  private readonly allMenuItems: MenuItem[] = [
    {
      id: Path.DASHBOARD,
      label: 'Dashboard',
      icon: 'dashboard',
      route: `/${Path.DASHBOARD}`,
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: 'admin',
      children: [
        {
          id: Path.SYNC_CONFIG,
          label: 'Sync Configuration',
          icon: 'sync-config',
          route: `/${Path.SYNC_CONFIG}`,
        },
        {
          id: Path.SYNCHRONIZATION,
          label: 'Synchronization',
          icon: 'synchronization',
          route: `/${Path.SYNCHRONIZATION}`,
        },
        {
          id: Path.USERS,
          label: 'Users',
          icon: 'users',
          route: `/${Path.USERS}`,
        },
        {
          id: Path.CUSTOM_VARIABLES,
          label: 'Custom Variables',
          icon: 'custom-variables',
          route: `/${Path.CUSTOM_VARIABLES}`,
        },
      ],
    },
    {
      id: Path.ORGANIZATION,
      label: 'Organization',
      icon: 'organization',
      route: `/${Path.ORGANIZATION}`,
    },
    {
      id: 'project-management',
      label: 'Project Management',
      icon: 'projects',
      children: [
        {
          id: Path.PROJECTS,
          label: 'Projects',
          icon: 'projects',
          route: `/${Path.PROJECTS}`,
        },
        {
          id: Path.OPPORTUNITIES,
          label: 'Opportunities',
          icon: 'opportunities',
          route: `/${Path.OPPORTUNITIES}`,
        },
        {
          id: Path.ACCOUNTS,
          label: 'Accounts',
          icon: 'clients',
          route: `/${Path.ACCOUNTS}`,
        },
        {
          id: Path.PROJECT_SETTINGS,
          label: 'Project Settings',
          icon: 'project-settings',
          route: `/${Path.PROJECT_SETTINGS}`,
        },
      ],
    },
    {
      id: 'resource-management',
      label: 'Resource Management',
      icon: 'resource-management',
      children: [
        {
          id: Path.RESOURCE_MANAGEMENT_TIMESHEET,
          label: 'Timesheet',
          icon: 'timesheet',
          route: `/${Path.RESOURCE_MANAGEMENT_TIMESHEET}`,
        },
        {
          id: Path.RESOURCE_MANAGEMENT_PERCENTAGE_ESTIMATIONS,
          label: 'Estimations',
          icon: 'timesheet',
          route: `/${Path.RESOURCE_MANAGEMENT_PERCENTAGE_ESTIMATIONS}`,
        },
      ],
    },
    {
      id: 'new-estimation',
      label: 'New Estimation',
      // Its own mark, so it does not read as a second Resource Management.
      icon: 'receipt-text',
      children: [
        {
          id: Path.RESOURCE_MANAGEMENT_OVERVIEW,
          label: 'Overview',
          icon: 'resource-management',
          route: `/${Path.RESOURCE_MANAGEMENT_OVERVIEW}`,
        },
        {
          id: Path.RESOURCE_MANAGEMENT_ESTIMATIONS,
          label: 'All Estimations',
          icon: 'timesheet',
          route: `/${Path.RESOURCE_MANAGEMENT_ESTIMATIONS}`,
        },
        {
          id: Path.RESOURCE_MANAGEMENT_JOB_TITLES,
          label: 'Job Titles',
          icon: 'resource-management',
          route: `/${Path.RESOURCE_MANAGEMENT_JOB_TITLES}`,
        },
        {
          id: Path.RESOURCE_MANAGEMENT_SETTINGS,
          label: 'Settings',
          icon: 'project-settings',
          route: `/${Path.RESOURCE_MANAGEMENT_SETTINGS}`,
        },
      ],
    },
    {
      id: Path.DOCUMENT_CENTRE,
      label: 'Documents Center',
      icon: 'document-centre',
      route: `/${Path.DOCUMENT_CENTRE}`,
    },
    {
      id: Path.REPORTS,
      label: 'Reports',
      icon: 'reports',
      children: [
        {
          id: Path.SYNC_REPORTS_PAGE,
          label: 'Sync Reports',
          icon: 'reports',
          route: `/${Path.REPORTS}/${Path.SYNC_REPORTS_PAGE}`,
        },
        {
          id: Path.FIXED_REPORTS,
          label: 'Fixed Reports',
          icon: 'reports',
          route: `/${Path.REPORTS}/${Path.FIXED_REPORTS}`,
        },
      ],
    },
  ];

  readonly menuItems = computed(() => {
    if (this.authService.canManageEstimationConfiguration()) {
      return this.allMenuItems;
    }

    return this.allMenuItems.map(item =>
      item.id === 'new-estimation'
        ? {
            ...item,
            children: item.children?.filter(
              child =>
                child.id !== Path.RESOURCE_MANAGEMENT_JOB_TITLES &&
                child.id !== Path.RESOURCE_MANAGEMENT_SETTINGS
            ),
          }
        : item
    );
  });

  handleLockClick(): void {
    const isLocked = this.encryptionService.getEncryptionState().isLocked;

    // Dynamic template switching based on encryption state
    if (isLocked) {
      this.openUnlockForm();
      return;
    }

    this.mainService.offCanvasConfig.set({
      show: true,
      title: 'Encryption Key Settings',
      hasCloseButton: true,
      template: this.lockTemplate() || null,
      notAllowedToClose: false,
      showLoader: false,
    });
  }

  /**
   * Opens the unlock form so the user can enter a (new) encryption key.
   */
  openUnlockForm(): void {
    this.mainService.offCanvasConfig.set({
      show: true,
      title: 'Encryption Key Settings',
      hasCloseButton: true,
      template: this.unlockTemplate() || null,
      notAllowedToClose: false,
      showLoader: false,
    });
  }

  /**
   * Handles successful unlock operation
   */
  handleUnlockSuccess(): void {
    // Close the offcanvas
    this.handleOffcanvasClose();
  }

  /**
   * Handles successful lock operation
   */
  handleLockSuccess(): void {
    // Close the offcanvas
    this.handleOffcanvasClose();
  }

  /**
   * Handles navigation to generate new key
   */
  handleGenerateNewKey(): void {
    this.mainService.offCanvasConfig.set({
      show: true,
      title: 'Encryption Key Settings',
      hasCloseButton: true,
      template: this.generateKeyTemplate() || null,
      notAllowedToClose: false,
      showLoader: false,
    });
  }

  /**
   * Handles successful key generation
   */
  handleGenerateKeySuccess(): void {
    // Close the offcanvas
    this.handleOffcanvasClose();
  }

  /**
   * Handles offcanvas close
   */
  handleOffcanvasClose(): void {
    this.mainService.offCanvasConfig.set({
      show: false,
      template: null,
    });
  }

  handleBellClick(): void {
    // Bell notification functionality (to be implemented)
  }

  handleMenuClick(item: MenuItem): void {
    if (item.route) {
      this.closeDrawer();
      this.router.navigate([item.route]);
    }
  }

  handleSignOut(): void {
    this.closeDrawer();
    this.authService.logout().subscribe();
  }

  closeOffCanvas(): void {
    if (this.mainService.offCanvasConfig().notAllowedToClose) return;
    this.mainService.offCanvasConfig().onClose?.();
    this.mainService.offCanvasConfig.set({ show: false, template: null });
  }
}
